import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCheck,
  CheckCircle2,
  Circle,
  CircleSlash,
  Clapperboard,
  Clock,
  Contact as ContactIcon,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  ListChecks,
  Lock,
  MapPin,
  Mic,
  MousePointerClick,
  Music,
  Reply,
  Sticker as StickerIcon,
  Video as VideoIcon,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

import { MediaGalleryDialog } from "../../echoflicks/MediaGalleryDialog";
import { isGalleryVideo } from "../../echoflicks/mediaGalleryUtils";
import { useDisplayableImage } from "../../echoflicks/useDisplayableImage";
import { useMediaUrl, type MediaUrlSource } from "../../echoflicks/useMediaUrl";
import type { EchoflicksSessionMedia } from "../../types";
import { FELIX } from "./felix";
import {
  UNSUPPORTED_MESSAGE_LABEL,
  describeMessagePrice,
  formatClockTime,
  isUnhandledOutcome,
  type ChatButton,
  type ChatButtonKind,
  type ChatButtonReply,
  type ChatDeliveryStatus,
  type ChatMedia,
  type ChatMessage,
  type ChatSender,
  type ChatTimelineItem,
  type EchoOutcome,
} from "./buildTimeline";
import {
  isScrolledToBottom,
  messageBounds,
  resolveScrollAdjustment,
  type ScrollAnchorSnapshot,
} from "./scrollAnchor";

// ---------------------------------------------------------------------------
// WhatsApp doodle wallpaper (subtle, theme-aware via dark:invert)
// ---------------------------------------------------------------------------

const DOODLE_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='256' height='256' viewBox='0 0 256 256'><g fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M20 30h34a6 6 0 0 1 6 6v16a6 6 0 0 1-6 6H38l-10 8v-8h-2a6 6 0 0 1-6-6V36a6 6 0 0 1 6-6z'/><path d='M120 40c-6-10-22-6-22 6 0 10 16 18 22 22 6-4 22-12 22-22 0-12-16-16-22-6z'/><rect x='180' y='28' width='44' height='30' rx='5'/><circle cx='202' cy='43' r='8'/><path d='M190 28l4-6h16l4 6'/><path d='M40 110v34M40 110l24-6v34'/><circle cx='34' cy='146' r='6'/><circle cx='58' cy='140' r='6'/><circle cx='128' cy='128' r='22'/><circle cx='120' cy='122' r='2'/><circle cx='136' cy='122' r='2'/><path d='M118 134q10 10 20 0'/><path d='M208 116l6 14 15 1-11 10 4 15-14-8-14 8 4-15-11-10 15-1z'/><path d='M30 210l8 8 16-18M46 210l8 8 16-18'/><path d='M120 196a40 40 0 0 0 40 40v-10l-12-6-8 8a30 30 0 0 1-12-12l8-8-6-12z'/><rect x='196' y='196' width='34' height='26' rx='4'/><path d='M230 202l14-6v26l-14-6'/></g></svg>`;
const DOODLE_URL = `url("data:image/svg+xml,${DOODLE_SVG}")`;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface WhatsAppChatProps {
  items: ChatTimelineItem[];
  header?: ReactNode;
  /** Tailwind height utility for the scroll area, e.g. "h-[600px]". */
  heightClassName?: string;
  emptyState?: ReactNode;
  /** Shown as a centered encryption-style notice at the top of the thread. */
  showEncryptionNotice?: boolean;
  /** Invoked when an echo divider's "View echo" affordance is clicked. */
  onEchoOpen?: (echoId: string | number) => void;
  className?: string;
  /** Rendered inside the scroll area, above the thread — e.g. a "Load older"
   *  control. When older messages are then prepended (the first message id
   *  changes while the last one stays), the visible offset is preserved by
   *  shifting `scrollTop` by the height that was added. */
  topSlot?: ReactNode;
  /** Rendered below the scroll area, outside it — e.g. a composer. */
  footer?: ReactNode;
}

/**
 * Auto-scroll contract: the thread opens pinned to the newest message and stays
 * pinned while the viewer is at the bottom. New messages re-pin only when the
 * viewer is already at the bottom or the newest message is the operator's own
 * send; prepended history keeps the viewer's place (see `scrollAnchor.ts`).
 */
export const WhatsAppChat = ({
  items,
  header,
  heightClassName = "h-[560px]",
  emptyState,
  showEncryptionNotice = true,
  onEchoOpen,
  className,
  topSlot,
  footer,
}: WhatsAppChatProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<ScrollAnchorSnapshot | null>(null);
  const atBottomRef = useRef(true);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState<number | null>(
    null,
  );
  const isEmpty = items.every((item) => item.type !== "message");
  const galleryItems = useMemo(() => {
    const seen = new Set<string>();
    const media: EchoflicksSessionMedia[] = [];
    for (const item of items) {
      if (item.type !== "message" || !item.message.mediaItem) continue;
      const key = String(item.message.mediaItem.id);
      if (seen.has(key)) continue;
      seen.add(key);
      media.push(item.message.mediaItem);
    }
    return media;
  }, [items]);

  useEffect(() => {
    if (
      activeGalleryIndex !== null &&
      activeGalleryIndex >= galleryItems.length
    ) {
      setActiveGalleryIndex(null);
    }
  }, [activeGalleryIndex, galleryItems.length]);

  const openMedia = (media: EchoflicksSessionMedia) => {
    const index = galleryItems.findIndex((item) => item.id === media.id);
    if (index >= 0) setActiveGalleryIndex(index);
  };

  // Pin to the newest message (WhatsApp opens at the bottom of the thread),
  // preserve the offset when history is prepended, and leave a reader who
  // scrolled up alone when polling only refreshes ticks or reactions.
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const bounds = messageBounds(items);
    const adjustment = resolveScrollAdjustment({
      previous: anchorRef.current,
      next: bounds,
      nextScrollHeight: node.scrollHeight,
      atBottom: atBottomRef.current,
    });
    if (adjustment.kind === "pin-bottom") {
      node.scrollTop = node.scrollHeight;
    } else if (adjustment.kind === "preserve" && adjustment.delta > 0) {
      node.scrollTop += adjustment.delta;
    }
    anchorRef.current = { ...bounds, scrollHeight: node.scrollHeight };
    atBottomRef.current = isScrolledToBottom(node);
  }, [items]);

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border border-black/10 dark:border-white/10",
        className,
      )}
    >
      {header}
      <div
        ref={scrollRef}
        onScroll={(event) => {
          atBottomRef.current = isScrolledToBottom(event.currentTarget);
        }}
        className={cn(
          "relative overflow-y-auto bg-[#efeae2] dark:bg-[#0b141a]",
          heightClassName,
        )}
      >
        {/* Doodle wallpaper overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.05] dark:opacity-[0.06] dark:invert"
          style={{ backgroundImage: DOODLE_URL, backgroundSize: "256px" }}
        />
        <div className="relative flex min-h-full flex-col px-3 py-3 sm:px-6">
          {topSlot ? (
            <div className="mb-2 flex justify-center">{topSlot}</div>
          ) : null}
          {isEmpty ? (
            <div className="flex flex-1 items-center justify-center py-12">
              {emptyState ?? null}
            </div>
          ) : (
            <>
              {showEncryptionNotice ? <EncryptionNotice /> : null}
              {items.map((item) => (
                <TimelineItemRenderer
                  key={item.id}
                  item={item}
                  onEchoOpen={onEchoOpen}
                  onMediaOpen={openMedia}
                />
              ))}
            </>
          )}
        </div>
      </div>
      {footer ? (
        <div className="border-t border-black/10 bg-[#f0f2f5] px-3 py-2 dark:border-white/10 dark:bg-[#202c33] sm:px-4">
          {footer}
        </div>
      ) : null}
      <MediaGalleryDialog
        items={galleryItems}
        activeIndex={activeGalleryIndex}
        onActiveIndexChange={setActiveGalleryIndex}
        title="WhatsApp media gallery"
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export interface WhatsAppChatHeaderProps {
  name: string;
  subtitle?: string | null;
  avatarUrl?: string | null;
  fallback?: string;
  actions?: ReactNode;
  /** Show the "via Felix · AI agent" identity chip on the right. */
  showAgentBadge?: boolean;
}

export const WhatsAppChatHeader = ({
  name,
  subtitle,
  avatarUrl,
  fallback,
  actions,
  showAgentBadge = true,
}: WhatsAppChatHeaderProps) => (
  <div className="flex items-center gap-3 border-b border-black/10 bg-[#f0f2f5] px-3 py-2 dark:border-white/10 dark:bg-[#202c33] sm:px-4">
    <Avatar className="size-10 shrink-0">
      {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
      <AvatarFallback className="bg-[#dfe5e7] text-sm text-[#54656f] dark:bg-[#2a3942] dark:text-[#aebac1]">
        {fallback ?? initialsOf(name)}
      </AvatarFallback>
    </Avatar>
    <div className="min-w-0 flex-1">
      <div className="truncate text-[15px] font-medium text-[#111b21] dark:text-[#e9edef]">
        {name}
      </div>
      {subtitle ? (
        <div className="truncate text-xs text-[#667781] dark:text-[#8696a0]">
          {subtitle}
        </div>
      ) : null}
    </div>
    {showAgentBadge ? (
      <div
        className="hidden items-center gap-2 rounded-full bg-white/70 px-2 py-1 dark:bg-white/10 sm:flex"
        title={`${FELIX.name} — AI agent replying on WhatsApp`}
      >
        <Avatar className="size-6">
          <AvatarImage src={FELIX.avatarUrl} alt={FELIX.name} />
          <AvatarFallback className="text-[10px]">FX</AvatarFallback>
        </Avatar>
        <span className="text-xs font-medium text-[#3b4a54] dark:text-[#aebac1]">
          via {FELIX.name}
        </span>
      </div>
    ) : null}
    {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
  </div>
);

// ---------------------------------------------------------------------------
// Item renderers
// ---------------------------------------------------------------------------

const TimelineItemRenderer = ({
  item,
  onEchoOpen,
  onMediaOpen,
}: {
  item: ChatTimelineItem;
  onEchoOpen?: (echoId: string | number) => void;
  onMediaOpen?: (media: EchoflicksSessionMedia) => void;
}) => {
  switch (item.type) {
    case "date":
      return <CenteredPill>{item.label}</CenteredPill>;
    case "echo-start":
      return <EchoStartDivider item={item} onEchoOpen={onEchoOpen} />;
    case "echo-end":
      return <EchoEndDivider item={item} onEchoOpen={onEchoOpen} />;
    case "message":
      if (item.message.sender === "system") {
        return <CenteredPill tone="system">{item.message.text}</CenteredPill>;
      }
      return (
        <MessageBubble
          message={item.message}
          groupedWithPrev={item.groupedWithPrev}
          showSenderName={item.showSenderName}
          onMediaOpen={onMediaOpen}
        />
      );
    default:
      return null;
  }
};

const EncryptionNotice = () => (
  <div className="mx-auto mb-2 flex max-w-md items-center gap-1.5 rounded-md bg-[#fdf4c5] px-3 py-1.5 text-center text-[12.5px] leading-snug text-[#54656f] shadow-sm dark:bg-[#182229] dark:text-[#ffd279]">
    <Lock className="size-3 shrink-0" />
    <span>
      Messages are end-to-end encrypted. This is a CRM mirror of the live
      WhatsApp thread.
    </span>
  </div>
);

const CenteredPill = ({
  children,
  tone = "date",
}: {
  children: ReactNode;
  tone?: "date" | "system";
}) => (
  <div className="my-2 flex justify-center">
    <span
      className={cn(
        "rounded-md px-3 py-1 text-center text-[12.5px] font-medium uppercase tracking-wide shadow-sm",
        tone === "date"
          ? "bg-white/90 text-[#54656f] dark:bg-[#182229] dark:text-[#8696a0]"
          : "bg-[#fdf4c5] normal-case text-[#54656f] dark:bg-[#182229] dark:text-[#8696a0]",
      )}
    >
      {children}
    </span>
  </div>
);

// --- Echo window dividers ---------------------------------------------------

const EchoStartDivider = ({
  item,
  onEchoOpen,
}: {
  item: Extract<ChatTimelineItem, { type: "echo-start" }>;
  onEchoOpen?: (echoId: string | number) => void;
}) => (
  <div className="my-2.5 flex justify-center">
    <div className="flex max-w-[88%] flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-lg border border-[#25d366]/40 bg-[#e7fce3] px-3 py-1.5 text-center text-[12.5px] text-[#1f7a4d] shadow-sm dark:border-[#25d366]/30 dark:bg-[#10231b] dark:text-[#7fe0a8]">
      <Clapperboard className="size-3.5 shrink-0" />
      <span className="font-semibold">Echo started</span>
      {item.title ? (
        <span className="truncate font-normal opacity-90">· {item.title}</span>
      ) : null}
      {item.echoId != null && onEchoOpen ? (
        <button
          type="button"
          onClick={() => onEchoOpen(item.echoId!)}
          className="font-medium underline underline-offset-2 hover:opacity-80"
        >
          View echo
        </button>
      ) : null}
    </div>
  </div>
);

const echoOutcomeMeta: Record<
  EchoOutcome,
  { label: string; Icon: typeof CheckCircle2; className: string }
> = {
  delivered: {
    label: "Echo delivered",
    Icon: CheckCircle2,
    className:
      "border-[#25d366]/40 bg-[#e7fce3] text-[#1f7a4d] dark:bg-[#10231b] dark:text-[#7fe0a8]",
  },
  failed: {
    label: "Echo failed",
    Icon: XCircle,
    className:
      "border-destructive/40 bg-destructive/5 text-destructive dark:bg-destructive/10",
  },
  cancelled: {
    label: "Echo cancelled",
    Icon: AlertTriangle,
    className:
      "border-amber-400/40 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
  },
  closed: {
    label: "End of echo window",
    Icon: Circle,
    className:
      "border-black/10 bg-white/80 text-[#54656f] dark:border-white/10 dark:bg-[#182229] dark:text-[#8696a0]",
  },
};

const EchoEndDivider = ({
  item,
  onEchoOpen,
}: {
  item: Extract<ChatTimelineItem, { type: "echo-end" }>;
  onEchoOpen?: (echoId: string | number) => void;
}) => {
  const meta = echoOutcomeMeta[item.outcome];
  const { Icon } = meta;
  return (
    <div className="mb-3 mt-1 flex justify-center">
      <div
        className={cn(
          "flex max-w-[88%] flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-lg border px-3 py-1.5 text-center text-[12.5px] shadow-sm",
          meta.className,
        )}
      >
        <Icon className="size-3.5 shrink-0" />
        <span className="font-semibold">{meta.label}</span>
        {item.echoId != null && onEchoOpen ? (
          <button
            type="button"
            onClick={() => onEchoOpen(item.echoId!)}
            className="font-medium underline underline-offset-2 hover:opacity-80"
          >
            Open
          </button>
        ) : null}
      </div>
    </div>
  );
};

// --- Message bubble ---------------------------------------------------------

const senderNameClass: Record<Exclude<ChatSender, "system">, string> = {
  contact: "text-[#1f7aad] dark:text-[#53bdeb]",
  felix: "text-[#1da57a] dark:text-[#06cf9c]",
  operator: "text-[#b26a00] dark:text-[#e8a33d]",
};

const MessageBubble = ({
  message,
  groupedWithPrev,
  showSenderName,
  onMediaOpen,
}: {
  message: ChatMessage;
  groupedWithPrev: boolean;
  showSenderName: boolean;
  onMediaOpen?: (media: EchoflicksSessionMedia) => void;
}) => {
  const isRight = message.side === "right";
  const sender = message.sender as Exclude<ChatSender, "system">;
  const showTail = !groupedWithPrev;
  const avatarUrl = sender === "felix" ? FELIX.avatarUrl : null;
  const hasReactions = message.reactions.length > 0;
  const unhandled = !isRight && isUnhandledOutcome(message.handlingOutcome);

  return (
    <div
      className={cn(
        "flex w-full items-end gap-2",
        groupedWithPrev ? "mt-0.5" : "mt-2.5",
        hasReactions && "mb-3",
        isRight ? "flex-row-reverse" : "flex-row",
      )}
    >
      {/* Outer-edge avatar, shown once per group */}
      <div className="w-7 shrink-0 self-end">
        {showTail ? (
          <Avatar className="size-7">
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt={message.senderName ?? ""} />
            ) : null}
            <AvatarFallback className="bg-[#dfe5e7] text-[10px] text-[#54656f] dark:bg-[#2a3942] dark:text-[#aebac1]">
              {initialsOf(message.senderName)}
            </AvatarFallback>
          </Avatar>
        ) : null}
      </div>

      <div
        className={cn(
          "relative max-w-[78%] px-2.5 py-1.5 text-[14.2px] leading-[19px] shadow-sm sm:max-w-[68%]",
          "rounded-lg",
          isRight
            ? "bg-[#d9fdd3] text-[#111b21] dark:bg-[#005c4b] dark:text-[#e9edef]"
            : "bg-white text-[#111b21] dark:bg-[#202c33] dark:text-[#e9edef]",
          showTail && isRight && "rounded-tr-none",
          showTail && !isRight && "rounded-tl-none",
        )}
      >
        {showTail ? <BubbleTail isRight={isRight} /> : null}

        {showSenderName ? (
          <div
            className={cn(
              "mb-0.5 text-[12.5px] font-semibold",
              senderNameClass[sender],
            )}
          >
            {message.senderName}
          </div>
        ) : null}

        {message.media ? (
          <MediaBlock
            media={message.media}
            source={message.mediaSource}
            mediaItem={message.mediaItem}
            onOpen={onMediaOpen}
          />
        ) : null}

        {message.buttonReply ? (
          <ButtonReplyChip reply={message.buttonReply} />
        ) : null}

        {message.text ? (
          <p
            className={cn(
              "whitespace-pre-wrap break-words",
              message.isCommand && "font-mono text-[13px]",
            )}
          >
            {message.text}
          </p>
        ) : null}

        {message.linkUrl ? (
          <a
            href={message.linkUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-0.5 inline-flex items-center gap-1 text-[13px] text-[#027eb5] underline underline-offset-2 hover:opacity-80 dark:text-[#53bdeb]"
          >
            <MapPin className="size-3.5" />
            Open in Maps
          </a>
        ) : null}

        {message.errorText ? (
          <p className="mt-1 flex items-start gap-1 break-words text-[12px] text-destructive">
            <AlertCircle className="mt-0.5 size-3 shrink-0" />
            {message.errorText}
          </p>
        ) : null}

        {unhandled ? (
          <span
            className="mt-1 inline-flex items-center gap-1 rounded bg-black/5 px-1.5 py-0.5 text-[11px] font-medium text-[#667781] dark:bg-white/10 dark:text-[#8696a0]"
            title={message.handlingOutcome ?? undefined}
          >
            <CircleSlash className="size-3" />
            {FELIX.name} didn&apos;t act on this
          </span>
        ) : null}

        <MessageMeta message={message} />

        {message.buttons.length > 0 ? (
          <MessageButtons buttons={message.buttons} />
        ) : null}

        {hasReactions ? (
          <span
            className={cn(
              "absolute -bottom-3 flex items-center gap-0.5 rounded-full border border-black/5 bg-white px-1.5 py-0.5 text-[12px] leading-none shadow-sm dark:border-white/10 dark:bg-[#202c33]",
              isRight ? "right-1" : "left-1",
            )}
            aria-label={`Reactions: ${message.reactions.join(" ")}`}
          >
            {message.reactions.map((emoji, index) => (
              <span key={`${emoji}-${index}`}>{emoji}</span>
            ))}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const buttonIcon: Record<ChatButtonKind, LucideIcon> = {
  reply: Reply,
  url: ExternalLink,
  flow: ListChecks,
  list: ListChecks,
  unknown: Reply,
};

/** WhatsApp draws message buttons as full-width blue rows under the bubble.
 *  Reply buttons are inert here (only the contact can tap them); CTA links
 *  open in a new tab so operators can inspect where a button pointed. */
const MessageButtons = ({ buttons }: { buttons: ChatButton[] }) => (
  <ul
    className="-mx-2.5 -mb-1.5 mt-1 divide-y divide-black/10 overflow-hidden rounded-b-lg border-t border-black/10 dark:divide-white/10 dark:border-white/10"
    aria-label="Message buttons"
  >
    {buttons.map((button, index) => {
      const Icon = buttonIcon[button.kind];
      const rowClassName =
        "flex w-full items-center justify-center gap-1.5 px-2.5 py-1.5 text-[13.5px] font-medium text-[#027eb5] dark:text-[#53bdeb]";
      const label = (
        <>
          <Icon className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{button.title}</span>
        </>
      );
      return (
        <li key={`${button.title}-${index}`}>
          {button.kind === "url" && button.url ? (
            <a
              href={button.url}
              target="_blank"
              rel="noreferrer noopener"
              title={button.url}
              className={cn(rowClassName, "hover:opacity-80")}
            >
              {label}
            </a>
          ) : (
            <span
              className={rowClassName}
              title={
                button.callbackData
                  ? `Reply payload: ${button.callbackData}`
                  : undefined
              }
            >
              {label}
            </span>
          )}
        </li>
      );
    })}
  </ul>
);

/** Marks an inbound bubble that answered a bot button or list, so a "1 Echo ·
 *  €0.99" reply reads as a tap rather than something the contact typed. */
const ButtonReplyChip = ({ reply }: { reply: ChatButtonReply }) => (
  <span
    className="mb-0.5 inline-flex items-center gap-1 rounded bg-black/5 px-1.5 py-0.5 text-[11px] font-medium text-[#667781] dark:bg-white/10 dark:text-[#8696a0]"
    title={reply.id ? `Reply payload: ${reply.id}` : undefined}
  >
    <MousePointerClick className="size-3" aria-hidden="true" />
    {reply.kind === "list" ? "Picked from a list" : "Tapped a button"}
  </span>
);

const BubbleTail = ({ isRight }: { isRight: boolean }) => (
  <span
    aria-hidden
    className={cn(
      "absolute top-0 block size-3",
      isRight
        ? "right-[-7px] text-[#d9fdd3] dark:text-[#005c4b]"
        : "left-[-7px] text-white dark:text-[#202c33]",
    )}
  >
    <svg viewBox="0 0 12 16" className="size-full fill-current">
      {isRight ? (
        <path d="M0 0 L12 0 L0 12 Z" />
      ) : (
        <path d="M12 0 L0 0 L12 12 Z" />
      )}
    </svg>
  </span>
);

const MessageMeta = ({ message }: { message: ChatMessage }) => {
  const time = formatClockTime(message.timestamp);
  // EMC-074: what Meta charged for an outbound message, beside its ticks.
  const price = describeMessagePrice(message.price);
  if (!time && !message.status && !price) return null;
  return (
    <span className="float-right ml-2 mt-1 flex translate-y-0.5 items-center gap-1 text-[11px] text-[#667781] dark:text-[#8696a0]">
      {price ? (
        <span
          className={cn(
            "rounded px-1 leading-4",
            price.tone === "charged"
              ? "bg-black/5 font-medium text-[#3b4a54] dark:bg-white/10 dark:text-[#d1d7db]"
              : "bg-black/5 dark:bg-white/10",
          )}
          title={price.title}
          data-testid="chat-message-price"
        >
          {price.label}
        </span>
      ) : null}
      {time}
      {message.status ? (
        <span
          className="inline-flex"
          data-testid="chat-message-status"
          data-status={message.status}
          title={TICK_LABELS[message.status]}
        >
          <Ticks status={message.status} />
        </span>
      ) : null}
    </span>
  );
};

const TICK_LABELS: Record<ChatDeliveryStatus, string> = {
  pending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed",
};

const Ticks = ({ status }: { status: ChatDeliveryStatus }) => {
  if (status === "pending") return <Clock className="size-3" />;
  if (status === "failed")
    return <AlertCircle className="size-3 text-destructive" />;
  if (status === "sent") return <Check className="size-3.5" />;
  return (
    <CheckCheck
      className={cn("size-3.5", status === "read" && "text-[#53bdeb]")}
    />
  );
};

const mediaIconFor = (media: ChatMedia) => {
  switch (media.kind) {
    case "image":
      return ImageIcon;
    case "video":
      return VideoIcon;
    case "voice":
      return Mic;
    case "audio":
      return Music;
    case "document":
      return FileText;
    case "sticker":
      return StickerIcon;
    case "location":
      return MapPin;
    case "contact":
      return ContactIcon;
    case "unsupported":
      return AlertTriangle;
    default:
      return FileText;
  }
};

const mediaLabelFor = (media: ChatMedia) =>
  media.detail ??
  (media.kind === "unsupported" ? UNSUPPORTED_MESSAGE_LABEL : media.label);

const MediaBlock = ({
  media,
  source,
  mediaItem,
  onOpen,
}: {
  media: ChatMedia;
  source: MediaUrlSource | null;
  mediaItem: EchoflicksSessionMedia | null;
  onOpen?: (media: EchoflicksSessionMedia) => void;
}) => {
  if ((media.kind === "image" || media.kind === "video") && mediaItem) {
    return (
      <DurableChatMediaPreview
        media={media}
        mediaItem={mediaItem}
        onOpen={onOpen}
      />
    );
  }
  if (media.kind === "image" && source) {
    return <ChatImage media={media} source={source} />;
  }
  return <MediaPlaceholder media={media} />;
};

const DurableChatMediaPreview = ({
  media,
  mediaItem,
  onOpen,
}: {
  media: ChatMedia;
  mediaItem: EchoflicksSessionMedia;
  onOpen?: (media: EchoflicksSessionMedia) => void;
}) => {
  const mediaUrl = useDisplayableImage(mediaItem);
  const video = isGalleryVideo(mediaItem);

  if (mediaUrl.url) {
    return (
      <button
        type="button"
        onClick={() => onOpen?.(mediaItem)}
        aria-label={`Open ${video ? "video" : "photo"} in gallery`}
        className="group relative mb-1 block w-full overflow-hidden rounded-lg bg-black text-left outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-ring"
      >
        {video ? (
          <video
            src={`${mediaUrl.url.split("#", 1)[0]}#t=0.001`}
            aria-hidden="true"
            muted
            playsInline
            preload="metadata"
            className="max-h-80 w-full object-cover transition group-hover:brightness-75"
            onError={mediaUrl.refresh}
          />
        ) : (
          <img
            src={mediaUrl.url}
            alt={media.label}
            loading="lazy"
            onError={mediaUrl.refresh}
            className="max-h-80 w-full object-cover transition group-hover:brightness-90"
          />
        )}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
          <span className="rounded-full bg-black/65 p-2 text-white">
            {video ? (
              <VideoIcon className="size-5" aria-hidden />
            ) : (
              <ImageIcon className="size-5" aria-hidden />
            )}
          </span>
        </span>
      </button>
    );
  }

  const Icon = mediaIconFor(media);
  return (
    <div className="mb-1 flex aspect-[4/3] w-full items-center justify-center gap-2 rounded-lg bg-black/5 text-[#54656f] dark:bg-white/5 dark:text-[#aebac1]">
      <Icon className="size-5 shrink-0" />
      <span className="text-[13px] font-medium">
        {mediaUrl.isLoading
          ? `Loading ${video ? "video" : "photo"}…`
          : media.label}
      </span>
    </div>
  );
};

const MediaPlaceholder = ({ media }: { media: ChatMedia }) => {
  const Icon = mediaIconFor(media);
  return (
    <div className="mb-1 flex items-center gap-2 rounded-md bg-black/5 px-2 py-2 dark:bg-white/5">
      <Icon className="size-4 shrink-0 text-[#54656f] dark:text-[#aebac1]" />
      <span className="text-[13px] font-medium text-[#54656f] dark:text-[#aebac1]">
        {mediaLabelFor(media)}
      </span>
    </div>
  );
};

/** Renders a user-submitted photo inline, signing the source via useMediaUrl.
 *  Falls back to the icon placeholder while loading or if the URL fails. */
const ChatImage = ({
  media,
  source,
}: {
  media: ChatMedia;
  source: MediaUrlSource;
}) => {
  const [failed, setFailed] = useState(false);
  const { url, isLoading } = useMediaUrl(source);

  if (url && !failed) {
    return (
      <img
        src={url}
        alt={media.label}
        loading="lazy"
        onError={() => setFailed(true)}
        className="mb-1 max-h-80 w-full rounded-lg object-cover"
      />
    );
  }

  const Icon = mediaIconFor(media);
  return (
    <div className="mb-1 flex aspect-[4/3] w-full items-center justify-center gap-2 rounded-lg bg-black/5 text-[#54656f] dark:bg-white/5 dark:text-[#aebac1]">
      <Icon className="size-5 shrink-0" />
      <span className="text-[13px] font-medium">
        {isLoading && !failed ? "Loading photo…" : media.label}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initialsOf(name?: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
